import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Upload } from 'lucide-react';
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription,
  DrawerBody, DrawerFooter,
  Button, IconButton, Input, Label, Textarea,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import {
  createVCard, updateVCard, uploadVCardAvatar, VCARD_SOCIAL_PLATFORMS,
  type VCard, type VCardInput, type VCardSocialInput,
} from '@/api/account';
import { AccountAvatar } from './AccountAvatar';

const EMPTY: VCardInput = { name: '', position: '', description: '', email: '', phone: '', address: '', avatar: '', socials: [] };

/**
 * Create / edit a VCard (slide-over), matching the datacentral-client Create
 * form: name · position · email · phone · address · description · avatar upload
 * (presigned) · up to 7 social links (platform + value).
 */
export function VCardFormDrawer({
  open, onOpenChange, editing,
}: { open: boolean; onOpenChange: (open: boolean) => void; editing: VCard | null }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<VCardInput>(EMPTY);
  const [preview, setPreview] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setFile(null);
    if (editing) {
      setForm({
        name: editing.name ?? '', position: editing.position ?? '', description: editing.description ?? '',
        email: editing.email ?? '', phone: editing.phone ?? '', address: editing.address ?? '',
        avatar: editing.avatar ?? '',
        socials: (editing.socials ?? []).map((s) => ({ key: s.key ?? s.platform ?? '', value: s.value ?? s.url ?? '' })),
      });
      setPreview(editing.avatar ?? '');
    } else {
      setForm(EMPTY);
      setPreview('');
    }
  }, [open, editing]);

  const set = (k: keyof VCardInput) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image')) return setError('Avatar must be an image.');
    if (f.size / 1_000_000 > 10) return setError('Avatar must be under 10MB.');
    setError('');
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const setSocial = (i: number, patch: Partial<VCardSocialInput>) =>
    setForm((f) => ({ ...f, socials: (f.socials ?? []).map((s, idx) => (idx === i ? { ...s, ...patch } : s)) }));
  const addSocial = () =>
    setForm((f) => ((f.socials?.length ?? 0) >= 7 ? f : { ...f, socials: [...(f.socials ?? []), { key: '', value: '' }] }));
  const removeSocial = (i: number) =>
    setForm((f) => ({ ...f, socials: (f.socials ?? []).filter((_, idx) => idx !== i) }));

  const mut = useMutation({
    mutationFn: async () => {
      let avatar = form.avatar;
      if (file) avatar = await uploadVCardAvatar(file);
      const payload: VCardInput = { ...form, avatar, socials: (form.socials ?? []).filter((s) => s.key && s.value) };
      return editing ? updateVCard(editing.id, payload) : createVCard(payload);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['account', 'vcards'] }); onOpenChange(false); },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Failed to save'),
  });

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent side="right" size="md">
        <DrawerHeader>
          <DrawerTitle>{editing ? 'Edit VCard' : 'New VCard'}</DrawerTitle>
          <DrawerDescription>Your shareable digital contact card.</DrawerDescription>
        </DrawerHeader>

        <DrawerBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {error ? <Alert tone="danger" title={error} /> : null}

          {/* Avatar upload */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)' }}>
            <AccountAvatar name={form.name || '?'} src={preview} size={64} />
            <div>
              <Button size="sm" variant="secondary" startIcon={<Upload size={15} />} onClick={() => fileRef.current?.click()}>
                Upload avatar
              </Button>
              {/* Hidden file input — no @ui file-input component exists */}
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickFile} />
              <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', marginTop: 'var(--vn-space-1)' }}>PNG/JPG, up to 10MB.</div>
            </div>
          </div>

          <Field label="Full name" required>{(p) => <Input {...p} value={form.name} onChange={set('name')} placeholder="Nguyễn Kim Thọ" />}</Field>
          <Field label="Position">{(p) => <Input {...p} value={form.position} onChange={set('position')} placeholder="Technology Leader" />}</Field>
          <Field label="Email">{(p) => <Input {...p} type="email" value={form.email} onChange={set('email')} placeholder="name@vnetwork.vn" />}</Field>
          <Field label="Phone">{(p) => <Input {...p} value={form.phone} onChange={set('phone')} placeholder="0987654321" />}</Field>
          <Field label="Address">{(p) => <Input {...p} value={form.address} onChange={set('address')} placeholder="Address" />}</Field>
          <Field label="Tagline">{(p) => <Textarea {...p} value={form.description} onChange={set('description')} rows={2} placeholder="A short tagline" />}</Field>

          {/* Socials */}
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Label>Social links</Label>
              <Button size="sm" variant="ghost" startIcon={<Plus size={14} />} onClick={addSocial} disabled={(form.socials?.length ?? 0) >= 7}>Add</Button>
            </div>
            {(form.socials ?? []).map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
                <Select value={s.key} onValueChange={(v) => setSocial(i, { key: v })}>
                  <SelectTrigger size="sm" style={{ width: 140 }}><SelectValue placeholder="Platform" /></SelectTrigger>
                  <SelectContent>
                    {VCARD_SOCIAL_PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input value={s.value} onChange={(e) => setSocial(i, { value: e.target.value })} placeholder="username or URL" style={{ flex: 1 }} />
                <IconButton variant="ghost" size="sm" aria-label="Remove" onClick={() => removeSocial(i)}><Trash2 size={15} /></IconButton>
              </div>
            ))}
          </div>
        </DrawerBody>

        <DrawerFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => mut.mutate()} loading={mut.isPending} disabled={!form.name.trim()}>{editing ? 'Save' : 'Create'}</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
